import { type ReactNode, useId } from 'react';

interface TooltipProps {
  /** The supporting sentence. Kept short — this is a footnote, not a panel. */
  label: ReactNode;
  children: ReactNode;
  /**
   * The child is already a control. It then keeps its own focus and its own accessible name — this
   * adds nothing but the hover panel, since wrapping a button in a focusable, dotted-underlined span
   * would give it a second tab stop and a decoration no control should carry.
   */
  onControl?: boolean;
}

/**
 * A hover-and-focus footnote on a word.
 *
 * CSS-only rather than state-driven: nothing here needs positioning logic or a portal, and a
 * tooltip that re-renders its parent on every mouseover is a cost for no gain. `group-focus-within`
 * is what makes it reachable by keyboard, which is why the trigger takes focus at all.
 *
 * Written here for the same reason as the rest of these primitives — this surface shares no UI with
 * the classic feed.
 */
export const Tooltip = ({ label, children, onControl = false }: TooltipProps) => {
  const id = useId();

  return (
    <span className="group/tip relative inline-flex">
      {onControl ? (
        children
      ) : (
        <span
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the trigger takes focus so the footnote is reachable without a pointer
          tabIndex={0}
          aria-describedby={id}
          className="cursor-help underline decoration-dotted decoration-from-font underline-offset-4"
        >
          {children}
        </span>
      )}
      <span
        role="tooltip"
        id={id}
        className="pointer-events-none invisible absolute bottom-full left-0 z-30 mb-1.5 w-64 rounded-md border border-border-neutral bg-bg-surface-overlay px-3 py-2 text-xs leading-snug font-normal tracking-normal text-text-neutral-primary normal-case opacity-0 shadow-lg transition-opacity group-focus-within/tip:visible group-focus-within/tip:opacity-100 group-hover/tip:visible group-hover/tip:opacity-100"
      >
        {label}
      </span>
    </span>
  );
};
